import { inject, Injectable, OnDestroy, Type } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { get } from 'lodash';
import { FrontSide, Material, Texture } from 'three';
import { H3Material } from '../materials/basic/h3-basic-material';
import { CalibrationService } from '../misc-services/calibration.service';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import {
  assignProjectionDefines,
  setProjectionUniforms,
} from '../utils/materials/material-utils';

interface TexturedMaterial extends Material {
  map: Texture | null;
}

// The default caching parameters are used to determine whether two materials
// should be considered the same. If a parameter is not specified, the default
// value is used. This means that if you want to differentiate a material, you
// should specify one of the following parameters with a value different than
// the default
const defaultCachingParameters = {
  side: FrontSide,
  color: 0xffffff,
  transparent: false,
  map: null,
  name: '',
};

@Injectable()
export class MaterialService implements OnDestroy {
  private materials = new Map<string, Material>();
  private currentOutputName!: string;

  private store = inject(PointCloudContentStore);
  private calibrationService = inject(CalibrationService);

  ngOnDestroy() {
    this.materials.forEach((material) => this.disposeMaterial(material));
    this.materials.clear();
  }

  setCurrentOutputName(outputName: string) {
    this.currentOutputName = outputName;
  }

  get<T extends H3Material>(
    constructor: Type<T>,
    parameters: Parameters<T['setValues']>[0] = {},
  ): T {
    let material: T;
    const key = this.getKey(constructor, parameters);

    if (!this.materials.has(key)) {
      material = new constructor();
      this.setProjection(material);
      this.materials.set(key, material);
    } else {
      material = this.materials.get(key) as T;
    }

    this.setProjectionUniforms(material);
    material.setValues(parameters);

    return material;
  }

  private setProjection(material: H3Material) {
    const state = this.store.state();
    const projectionType = this.calibrationService.getProjectionType(
      this.currentOutputName,
    );
    const { enable_motion_compensation } = state.output.meta;

    assignProjectionDefines(material, projectionType);
    material.assignDefines({
      USE_MOTION_COMPENSATION: enable_motion_compensation === true,
    });
  }

  private setProjectionUniforms(material: H3Material) {
    const state = this.store.state();
    const { calibrationData, projectionImageSize } = state;

    // prevents error when fetching calibration data for non-projection output
    if (
      !calibrationData.hasOwnProperty(this.currentOutputName) ||
      !projectionImageSize.hasOwnProperty(this.currentOutputName)
    ) {
      return;
    }

    const projectionType = this.calibrationService.getProjectionType(
      this.currentOutputName,
    );

    setProjectionUniforms(material, {
      state,
      currentOutputName: this.currentOutputName,
      projectionType,
    });
  }

  private getKey<T extends Material>(
    constructor: Type<T>,
    parameters: Parameters<T['setValues']>[0],
  ) {
    const obj = { ...defaultCachingParameters, ...parameters };
    const cachingKeys = Object.keys(defaultCachingParameters).sort((a, b) =>
      a.localeCompare(b),
    );

    const projectionType = this.calibrationService.getProjectionType(
      this.currentOutputName,
    );

    return `${constructor.name}-projectionType:${projectionType}-${cachingKeys
      .map((key) => `${key}:${stringifyParameters(get(obj, key))}`)
      .join(',')}`;
  }

  private disposeMaterial(material: Material) {
    if (isTexturedMaterial(material)) {
      material.map?.dispose();
    }
    material.dispose();
  }
}

function stringifyParameters(parameter: unknown) {
  if (parameter instanceof Texture) {
    return parameter.uuid;
  }
  return JSON.stringify(parameter);
}

function isTexturedMaterial(material: Material): material is TexturedMaterial {
  // ThreeJS uses null as a default value
  return 'map' in material && isDefined(material.map);
}
